import { useEffect, useRef } from 'react'
import { animate, useMotionValue, useTransform, type MotionValue } from 'framer-motion'
import { refreshInteractiveBounds } from '@highlight/ui'
import { notchCollapseTransition, notchResizeTransition, notchToastEnterTransition } from './notch-motion'

export type NotchShapeMode = 'row' | 'collapsed' | 'toast'

/** Extents are measured outward from the physical notch's center line. */
export interface NotchShapeTarget {
  left: number
  right: number
  height: number
  radius: number
}

interface NotchShape {
  left: MotionValue<number>
  right: MotionValue<number>
  width: MotionValue<number>
  height: MotionValue<number>
  radius: MotionValue<number>
}

/**
 * Drives the notch cutout's bounds from one set of motion values so the black shape, its fillets,
 * and the counter-translated content anchor all move on the exact same frames.
 */
export function useNotchShape(target: NotchShapeTarget, mode: NotchShapeMode): NotchShape {
  const left = useMotionValue(target.left)
  const right = useMotionValue(target.right)
  const height = useMotionValue(target.height)
  const radius = useMotionValue(target.radius)
  const width = useTransform(() => left.get() + right.get())
  const prevModeRef = useRef(mode)

  useEffect(() => {
    const prevMode = prevModeRef.current
    prevModeRef.current = mode
    const transition =
      mode === 'toast'
        ? notchToastEnterTransition
        : mode === 'collapsed' || prevMode === 'toast'
          ? notchCollapseTransition
          : notchResizeTransition

    refreshInteractiveBounds()
    const controls = [
      animate(left, target.left, transition),
      animate(right, target.right, transition),
      animate(height, target.height, transition),
      animate(radius, target.radius, transition),
    ]
    void Promise.all(controls).then(refreshInteractiveBounds)
    return () => controls.forEach((control) => control.stop())
  }, [height, left, mode, radius, right, target.height, target.left, target.radius, target.right])

  return { left, right, width, height, radius }
}
